import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Card, CardHeader } from 'reactstrap';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import 'datatables.net-bs5';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { H4 } from '../../../AbstractElements';
import { ReclamationTableHeader } from '../../../Constant';
import ManageReclamation from '../../../api/ManageReclamation';

const reclamationStatusBadge = (status) => {
    switch (status) {
        case 'PENDING':   return '<span class="badge bg-warning text-dark">EN ATTENTE</span>';
        case 'VALIDATED': return '<span class="badge bg-success">VALIDÉE</span>';
        case 'REJECTED':  return '<span class="badge bg-danger">REJETÉE</span>';
        default:          return `<span class="badge bg-dark">${status}</span>`;
    }
};

const ReclamationsTab = () => {
    const tableRef = useRef(null);
    const [reclamations, setReclamations] = useState([]);
    const [loadingReclamation, setLoadingReclamation] = useState(null);

    const loadReclamations = () => {
        ManageReclamation.loadAllReclamations()
            .then((response) => {
                const meta = response.data.response;
                if (meta.status_code === 200) {
                    const data = response.data.data !== undefined ? response.data.data : meta.data;
                    setReclamations(Object.values(data || {}).flat());
                } else {
                    toast.error(meta.message || "Erreur lors du chargement des réclamations.");
                }
            })
            .catch(() => {
                toast.error("Erreur lors du chargement des données. Réessayer plus tard.");
            });
    };

    const handleAction = async (reclamationId, reclamationStatus) => {
        setLoadingReclamation(reclamationId + reclamationStatus);
        try {
            const response = await ManageReclamation.validateReclamation({
                reclamation_id: reclamationId,
                reclamation_status: reclamationStatus,
            });
            const meta = response.data.response;
            if (meta.status_code === 200) {
                toast.success(meta.message || `Réclamation ${reclamationStatus === 'VALIDATED' ? 'validée' : 'rejetée'} avec succès`);
                loadReclamations();
            } else if (meta.status_code === 422) {
                const firstError = meta.errors ? Object.values(meta.errors)[0]?.[0] : null;
                toast.error(firstError || meta.message || "Validation échouée.");
            } else {
                toast.error(meta.message || "Erreur lors de la mise à jour");
            }
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setLoadingReclamation(null);
        }
    };

    useEffect(() => {
        const $table = $(tableRef.current);

        if (!$.fn.DataTable.isDataTable(tableRef.current)) {
            $table.DataTable({
                order: [[5, 'desc']],
                language: {
                    processing:     "Traitement en cours...",
                    search:         "Rechercher&nbsp;:",
                    lengthMenu:     "Afficher _MENU_ éléments",
                    info:           "Affichage de l'élément _START_ à _END_ sur _TOTAL_ éléments",
                    infoEmpty:      "Affichage de l'élément 0 à 0 sur 0 élément",
                    infoFiltered:   "(filtré de _MAX_ éléments au total)",
                    infoPostFix:    "",
                    loadingRecords: "Chargement en cours...",
                    zeroRecords:    "Aucun élément à afficher",
                    emptyTable:     "Aucune donnée disponible dans le tableau",
                    paginate: {
                        first:      "Premier",
                        previous:   "Précédent",
                        next:       "Suivant",
                        last:       "Dernier"
                    },
                    aria: {
                        sortAscending:  ": activer pour trier la colonne par ordre croissant",
                        sortDescending: ": activer pour trier la colonne par ordre décroissant"
                    }
                },
                data: [],
                columns: ReclamationTableHeader.map((header) => ({ title: header })),
            });
        }

        loadReclamations();

        return () => {
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                $table.DataTable().destroy();
            }
        };
    }, []);

    useEffect(() => {
        if ($.fn.DataTable.isDataTable(tableRef.current)) {
            const table = $(tableRef.current).DataTable();
            table.clear();

            reclamations.forEach((reclamation) => {
                const isActionable = reclamation.reclamation_status === 'PENDING';

                table.row.add([
                    reclamation.client?.complete_name || '',
                    reclamation.client?.phone_number || '',
                    reclamation.reclamation_number || '',
                    reclamation.reclamation_amount,
                    reclamationStatusBadge(reclamation.reclamation_status),
                    reclamation.created_at,
                    `
                        <button class='btn btn-sm btn-primary validate-btn'
                                data-id='${reclamation.id}'
                                data-status='VALIDATED'
                                ${!isActionable ? 'disabled' : ''}>
                          ${loadingReclamation === reclamation.id + 'VALIDATED'
                                ? "<span class='spinner-border spinner-border-sm'></span>"
                                : "<i class='fa fa-check'></i> Valider"}
                        </button>
                        <button class='btn btn-sm btn-danger reject-btn'
                                data-id='${reclamation.id}'
                                data-status='REJECTED'
                                ${!isActionable ? 'disabled' : ''}>
                          ${loadingReclamation === reclamation.id + 'REJECTED'
                                ? "<span class='spinner-border spinner-border-sm'></span>"
                                : "<i class='fa fa-times'></i> Rejeter"}
                        </button>
                    `
                ]);
            });
            table.draw();

            $(tableRef.current).off('click', '.validate-btn, .reject-btn').on('click', '.validate-btn, .reject-btn', function () {
                const id = $(this).data('id');
                const status = $(this).data('status');
                handleAction(id, status);
            });
        }
    }, [reclamations, loadingReclamation]);

    return (
        <Fragment>
            <ToastContainer />
            <Card>
                <CardHeader>
                    <H4 attrH4={{ className: 'card-title mb-0' }}>
                        Tableau de la liste des réclamations
                    </H4>
                </CardHeader>
                <div className='table-responsive edit-table'>
                    <table
                        ref={tableRef}
                        className='display table table-striped table-vcenter text-nowrap'
                        style={{ width: '100%' }}
                    >
                        <thead>
                        <tr>
                            {ReclamationTableHeader.map((header, i) => (
                                <th key={i}>{header}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody></tbody>
                    </table>
                </div>
            </Card>
        </Fragment>
    );
};

export default ReclamationsTab;
